import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { radius } from '@karehero/llama/theme/radius';

import { Box, Stack } from '@mui/material';

import ComputerFrame from '@/designs-dev/device/ComputerFrame';
import ScaleToFit from '@/designs-dev/device/ScaleToFit';
import { PhoneScreenContext } from '@/designs-dev/device/PhoneScreenContext';
import {
  computerScreenHeight,
  computerScreenWidth,
  type Viewport,
} from '@/designs-dev/device/viewport';
import {
  providerScenes,
  type ProviderFrame,
} from '@/cam-admin/paywall/Provider.scenes';
import {
  F2Email,
  F3Paywall,
  F4Answers,
  F6Unlocked,
  F11Timeout,
  S2PaymentFailed,
  S4Declined,
} from '@/cam-admin/explore/paywall/Flow';
import {
  InspectProvider,
  useInspect,
} from '@/designs-dev/inspect/InspectContext';
import InspectOverlay from '@/designs-dev/inspect/InspectOverlay';
import InspectPanel from '@/designs-dev/inspect/InspectPanel';
import LayersPanel from '@/designs-dev/inspect/LayersPanel';
import DrillModeToggle from '@/designs-dev/inspect/DrillModeToggle';
import StageBar, { STAGE_BAR_ROOM } from '@/designs-dev/inspect/StageBar';
import StateSelector from '@/designs-dev/inspect/StateSelector';
import { useScreenSelect } from '@/designs-dev/loft/ScreenSelectContext';

// The provider's side of the paywall, on a stage — what happens after the care
// request email goes out.
//
// A sibling to ShortlistStage, which is the CAM's side of the same flow. The two
// halves were only ever walkable together inside the exploration writeup, and a
// writeup is the wrong shape for this: a provider meets one screen at a time, so
// the scene select is how you meet them. Same frames, no redraw — they stay
// defined once in Flow.tsx and both surfaces render those.
//
// The frames own their own chrome (the provider is on a KareHero web page, or in
// their mail client for the email), so this stage supplies only the laptop and
// the inspect rig.
// `go` lets a frame hand the reader on to the screen its own controls promise,
// so the journey is walkable end to end: inbox → blurred profile → answers →
// checkout → unlocked. Checkout is not a stop on that walk: it is the rail on
// the answers screen, so paying happens where the answering does. Declining is a
// stop on the walk in its own right, reachable from the blurred profile and from
// the answers screen — every screen where a provider knows enough about the lead
// to say no to it, and not the email, where they do not. The handlers are optional
// on every frame, so the exploration writeup still renders them as pictures rather
// than steps.
const FRAMES: Record<
  ProviderFrame,
  (go: (frame: ProviderFrame) => void) => ReactNode
> = {
  email: (go) => <F2Email onView={() => go('paywall')} />,
  paywall: (go) => (
    <F3Paywall
      onUnlock={() => go('answers')}
      onDecline={() => go('declined')}
    />
  ),
  answers: (go) => (
    <F4Answers onPay={() => go('unlocked')} onDecline={() => go('declined')} />
  ),
  answersReturning: (go) => (
    <F4Answers
      isReturning
      onPay={() => go('unlocked')}
      onDecline={() => go('declined')}
    />
  ),
  // Also where a provider who has already paid lands when they come back to
  // their own link. Their lead did not go anywhere, so there is nothing to draw
  // for that case beyond sending them here.
  unlocked: () => <F6Unlocked />,
  declined: () => <S4Declined />,
  timeout: () => <F11Timeout />,
  failed: () => <S2PaymentFailed />,
};

const Stage = () => {
  const { active, current, setActive, setCurrent, setHovered } = useInspect();
  const screenSelect = useScreenSelect();

  const [searchParams, setSearchParams] = useSearchParams();
  const [sceneId, setSceneId] = useState(
    () => searchParams.get('scene') ?? providerScenes[0].id,
  );

  // Clamp to a scene that exists, so a stale or hand-typed ?scene never breaks
  // the page.
  const currentScene =
    providerScenes.find((s) => s.id === sceneId) ?? providerScenes[0];

  // Reflect the active scene in the URL so a state is a shareable address.
  useEffect(() => {
    if (searchParams.get('scene') !== currentScene.id) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('scene', currentScene.id);
          return next;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScene.id]);

  // A web design opens at the laptop screen's width with no laptop drawn round
  // it: same screen as the frame, judged at the size it renders. The frame is
  // still a click away in the toggle for showing the thing off.
  const [viewport, setViewport] = useState<Viewport>(computerScreenWidth);
  const framed = viewport === 'frame';
  const [fullEl, setFullEl] = useState<HTMLElement | null>(null);

  // Esc is staged: a first press clears a selected layer, a second leaves drill
  // mode. Matches the other stages.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (current) {
        setCurrent(null);
        setHovered(null);
      } else {
        setActive(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, current, setActive, setCurrent, setHovered]);

  // Switching state clears any pinned component, so a ?select doesn't linger
  // onto the new (remounted) scene's nodes.
  const pickScene = (id: string) => {
    setCurrent(null);
    setHovered(null);
    setSceneId(id);
  };

  // Following a link inside a frame moves the scene select with it, so the URL
  // and the panel keep agreeing with what is on screen. Same reset as switching
  // by hand, so a pinned layer never lingers onto the next screen's nodes.
  const go = (frame: ProviderFrame) => {
    const next = providerScenes.find((s) => s.initial.frame === frame);
    if (!next) return;
    setCurrent(null);
    setHovered(null);
    setSceneId(next.id);
  };

  // Keyed so a scene always starts where it says rather than wherever the last
  // one was clicked to.
  const page = (
    <Box key={currentScene.id}>{FRAMES[currentScene.initial.frame](go)}</Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: active ? 'custom.charcoal' : 'custom.neutral.100',
        transition: 'background-color 400ms ease',
      }}
    >
      {/* The stage, with the controls floating over its foot. */}
      <Box sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            p: 3,
            // Room for the floating controls under the screen.
            pb: STAGE_BAR_ROOM,
            overflow: 'auto',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              alignItems: framed ? 'center' : 'stretch',
              justifyContent: 'center',
            }}
          >
            {/* The Stack must span the full stage width — the laptop measures its
              wrapper to size itself, and an auto-width parent would make it
              measure (and feed back on) its own width. */}
            <Stack sx={{ alignItems: 'center', width: '100%', minHeight: 0 }}>
              {framed ? (
                <ComputerFrame>
                  {page}
                  <InspectOverlay />
                </ComputerFrame>
              ) : (
                /* Out of the frame. 'fill' takes the stage's own width and
                 height; a fixed width lays out at that width and is scaled
                 down to whatever room there is, so a 1440 design is judged at
                 1440 without running off the side of the stage. */
                <ScaleToFit
                  width={viewport === 'fill' ? 'fill' : viewport}
                  height={
                    viewport === 'fill' ? undefined : computerScreenHeight
                  }
                >
                  <Box
                    sx={{
                      position: 'relative',
                      height: '100%',
                      width: '100%',
                      overflow: 'hidden',
                      borderRadius: `${radius.md}px`,
                      border: 1,
                      borderColor: 'custom.neutral.200',
                      bgcolor: 'background.default',
                    }}
                    ref={(el: HTMLElement | null) => setFullEl(el)}
                  >
                    <PhoneScreenContext.Provider value={fullEl}>
                      {page}
                      <InspectOverlay />
                    </PhoneScreenContext.Provider>
                  </Box>
                </ScaleToFit>
              )}
            </Stack>
          </Box>
        </Box>

        {/* How you are looking at the design, whose view of the flow it is,
            then what that screen is showing. The middle one is only there when
            this screen is one of several — the route builds it, because it is
            the route that knows the siblings. */}
        <StageBar>
          <DrillModeToggle viewport={viewport} onViewportChange={setViewport} />
          {screenSelect}
          <StateSelector
            scenes={providerScenes}
            sceneId={currentScene.id}
            onSceneChange={pickScene}
          />
        </StageBar>
      </Box>

      <LayersPanel />
      <InspectPanel />
    </Box>
  );
};

const ProviderStage = () => (
  <InspectProvider>
    <Stage />
  </InspectProvider>
);

export default ProviderStage;
